import React, { useState } from 'react';
import { CartItem, OrderOutlet } from '../../../types';
import { formatRupiah } from '../../../utils/formatters';
import { resolveMediaUrl } from '../../../utils/api';
import { generateCartItemId, calculateItemUnitPrice } from '../../../data/addOnsData';
import { FIXED_SERVICE_FEE } from '../../../utils/deliveryFee';
import {
  X,
  Trash2,
  Plus,
  Minus,
  ShoppingBag,
  ArrowRight,
  ArrowLeft,
  FileText,
  Store,
  Layers,
  Sparkles,
  Droplets,
  AlertTriangle,
} from 'lucide-react';
import { motion } from 'motion/react';
import { isMenuItemAvailableForOutlet } from '../../../utils/supabaseStock';

interface OrderCartDrawerProps {
  outlet: OrderOutlet;
  cart: CartItem[];
  onUpdateQuantity: (cartItemId: string, delta: number) => void;
  onRemoveItem: (cartItemId: string) => void;
  onUpdateNote: (cartItemId: string, note: string) => void;
  generalNote: string;
  onUpdateGeneralNote: (note: string) => void;
  onClose: () => void;
  onContinue: () => void;
}

export const OrderCartDrawer: React.FC<OrderCartDrawerProps> = ({
  outlet,
  cart,
  onUpdateQuantity,
  onRemoveItem,
  onUpdateNote,
  generalNote,
  onUpdateGeneralNote,
  onClose,
  onContinue,
}) => {
  const [editingNoteForId, setEditingNoteForId] = useState<string | null>(null);

  const getItemId = (item: CartItem): string => {
    return (
      item.id ||
      generateCartItemId(item.product.id, item.size?.name, item.topping?.name, item.syrup?.name, item.customOptions)
    );
  };

  const getItemUnitPrice = (item: CartItem): number => {
    return calculateItemUnitPrice(item?.product?.price ?? 0, item?.size, item?.topping, item?.syrup, item?.customOptions);
  };

  const safeCart = cart || [];
  const subtotal = safeCart.reduce(
    (acc, item) => acc + getItemUnitPrice(item) * (item?.quantity || 1),
    0
  );
  const totalItems = safeCart.reduce((acc, item) => acc + (item?.quantity || 0), 0);

  const hasUnavailableItems = safeCart.some(
    (item) => item?.product && !isMenuItemAvailableForOutlet(item.product, outlet?.id)
  );

  const fallbackCartImg =
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/gen_f7d71a9d8c_42ee7204a6eea867.png';

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex justify-end bg-[#0A192F]/50 backdrop-blur-sm animate-fadeIn">
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="w-full max-w-lg bg-[#F8FAFC] text-[#0A192F] h-full flex flex-col justify-between shadow-2xl"
      >
        {/* Header — Exact UX Pilot Shopping Cart Header */}
        <header 
          className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E2E8F0] px-4 flex items-center justify-between shrink-0"
          style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))', paddingBottom: '1rem' }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Kembali ke Menu"
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0A192F] cursor-pointer hover:border-[#0056D2]/40 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-lg font-bold text-[#0A192F]">Shopping Cart</h1>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold text-[#0056D2] cursor-pointer"
          >
            + Add More
          </button>
        </header>

        {/* Cart Items List */}
        <main className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Pickup Location Box */}
          <div className="bg-white rounded-2xl p-4 border border-[#E2E8F0] flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-blue-50 text-[#0056D2] rounded-xl flex items-center justify-center shrink-0">
                <Store className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-slate-400 uppercase">
                  Pickup from
                </p>
                <p className="text-sm font-bold text-[#0A192F] truncate">
                  {outlet?.name || 'Leton Sudirman Central'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-bold text-[#0056D2] cursor-pointer shrink-0"
            >
              Menu
            </button>
          </div>

          {cart.length === 0 ? (
            <div className="py-16 text-center bg-white rounded-2xl border border-[#E2E8F0] p-6 flex flex-col items-center justify-center">
              <div className="w-16 h-16 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center text-slate-400 mb-4">
                <ShoppingBag className="w-8 h-8" />
              </div>
              <p className="font-bold text-[#0A192F] text-base">Your Cart is Empty</p>
              <p className="text-slate-500 text-xs mt-1 max-w-xs">
                Pilih menu kopi atau minuman favorit Anda untuk memulai pesanan.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-6 px-5 py-2.5 rounded-xl bg-[#0056D2] text-white text-xs font-bold hover:bg-blue-700 transition-colors cursor-pointer"
              >
                Browse Menu
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {cart.map((item) => {
                  const itemId = getItemId(item);
                  const unitPrice = getItemUnitPrice(item);
                  const isEditingNote = editingNoteForId === itemId;
                  const hasSize = item.size && item.size.name;
                  const hasTopping = item.topping && item.topping.name !== 'No Topping';
                  const hasSyrup = item.syrup && item.syrup.name !== 'No Syrup';
                  const isItemAvailableInOutlet = isMenuItemAvailableForOutlet(
                    item.product,
                    outlet?.id
                  );

                  const optionsSummary = [
                    hasSize ? item.size!.name : null,
                    hasTopping ? `+ ${item.topping!.name}` : null,
                    hasSyrup ? `+ ${item.syrup!.name}` : null,
                    ...(item.customOptions || []).map((co) => co.optionName),
                  ]
                    .filter(Boolean)
                    .join(' • ');

                  return (
                    <div
                      key={itemId}
                      className={`bg-white rounded-2xl p-4 border transition-all space-y-3 ${
                        isItemAvailableInOutlet
                          ? 'border-[#E2E8F0]'
                          : 'border-rose-300 bg-rose-50/40'
                      }`}
                    >
                      <div className="flex gap-4">
                        <div className="w-20 h-20 bg-[#F8FAFC] rounded-xl overflow-hidden flex-shrink-0 relative border border-[#E2E8F0]">
                          <img
                            className="w-full h-full object-cover"
                            src={resolveMediaUrl(item.product.image) || fallbackCartImg}
                            alt={item.product.name}
                            onError={(e) => {
                              const target = e.currentTarget;
                              if (target.src !== fallbackCartImg) {
                                target.src = fallbackCartImg;
                              }
                            }}
                          />
                        </div>

                        <div className="flex-1 flex flex-col justify-between min-w-0">
                          <div>
                            <div className="flex justify-between items-start gap-2">
                              <h3 className="font-bold text-sm text-[#0A192F] truncate">
                                {item.product.name}
                              </h3>
                              <button
                                type="button"
                                onClick={() => onRemoveItem(itemId)}
                                className="text-slate-300 hover:text-red-500 transition-colors cursor-pointer shrink-0"
                                title="Hapus menu"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-2">
                              {optionsSummary || 'Standard Recipe'}
                            </p>
                            {!isItemAvailableInOutlet && (
                              <span className="inline-block mt-1 px-2 py-0.5 rounded bg-rose-100 text-rose-700 text-[9px] font-bold uppercase">
                                Sold Out at Outlet
                              </span>
                            )}
                          </div>

                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm font-bold text-[#0056D2]">
                              {formatRupiah(unitPrice * item.quantity)}
                            </span>

                            <div className="flex items-center gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-2 py-1">
                              <button
                                type="button"
                                onClick={() => onUpdateQuantity(itemId, -1)}
                                className="text-xs text-slate-400 hover:text-[#0A192F] cursor-pointer"
                                aria-label="Kurangi"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="text-xs font-bold text-[#0A192F]">
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                onClick={() => onUpdateQuantity(itemId, 1)}
                                className="w-5 h-5 rounded bg-[#0A192F] text-white flex items-center justify-center cursor-pointer"
                                aria-label="Tambah"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Item Note */}
                      <div className="pt-2 border-t border-[#E2E8F0]/60 flex items-center justify-between text-xs">
                        {isEditingNote ? (
                          <div className="w-full flex items-center gap-2">
                            <input
                              type="text"
                              value={item.note || ''}
                              onChange={(e) => onUpdateNote(itemId, e.target.value)}
                              placeholder="Catatan item (misal: less ice)..."
                              className="flex-1 px-3 py-1.5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-[#0A192F] text-base sm:text-xs focus:outline-none focus:border-[#0056D2]"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => setEditingNoteForId(null)}
                              className="px-2.5 py-1.5 rounded-lg bg-[#0056D2] text-white text-[11px] font-bold cursor-pointer"
                            >
                              Save
                            </button>
                          </div>
                        ) : (
                          <div className="w-full flex items-center justify-between">
                            <span className="text-slate-400 text-[11px] truncate max-w-[240px]">
                              {item.note ? `Note: "${item.note}"` : 'No special item note'}
                            </span>
                            <button
                              type="button"
                              onClick={() => setEditingNoteForId(itemId)}
                              className="text-[#0056D2] font-semibold text-[11px] shrink-0 cursor-pointer"
                            >
                              {item.note ? 'Edit Note' : '+ Add Note'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Notes for Barista */}
              <div className="bg-white rounded-2xl p-4 border border-[#E2E8F0] space-y-2">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                  <FileText className="w-3.5 h-3.5 text-[#0056D2]" />
                  <span>Notes for Barista</span>
                </div>
                <textarea
                  rows={2}
                  value={generalNote}
                  onChange={(e) => onUpdateGeneralNote(e.target.value)}
                  placeholder="Example: No lid please, or make it extra hot..."
                  className="w-full px-3 py-2 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0A192F] text-base sm:text-xs placeholder:text-slate-400 focus:outline-none focus:border-[#0056D2] resize-none"
                />
              </div>
            </>
          )}
        </main>

        {/* Bottom Summary & Checkout — Exact UX Pilot Cart Footer */}
        {cart.length > 0 && (
          <div
            className="bg-white border-t border-[#E2E8F0] p-6 rounded-t-[32px] shadow-[0_-8px_24px_rgba(0,0,0,0.05)] z-50 shrink-0"
            style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
          >
            {hasUnavailableItems && (
              <div className="mb-4 p-3 rounded-xl bg-rose-50 border border-rose-200 flex items-center gap-2 text-rose-700 text-xs">
                <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
                <span>
                  Ada menu yang habis di outlet ini. Hapus item bertanda Sold Out untuk melanjutkan.
                </span>
              </div>
            )}

            <div className="space-y-2 mb-6">
              <div className="flex justify-between items-center text-slate-500">
                <span className="text-xs">Subtotal Pesanan ({totalItems} item)</span>
                <span className="text-xs font-semibold text-[#0A192F]">
                  {formatRupiah(subtotal)}
                </span>
              </div>
              <div className="flex justify-between items-center text-slate-500">
                <span className="text-xs">Biaya Layanan</span>
                <span className="text-xs font-semibold text-[#0A192F]">
                  {formatRupiah(FIXED_SERVICE_FEE)}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-[#E2E8F0]">
                <span className="text-base font-bold text-[#0A192F]">Total Pembayaran</span>
                <span className="text-xl font-bold text-[#0056D2]">
                  {formatRupiah(subtotal + FIXED_SERVICE_FEE)}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={onContinue}
              disabled={hasUnavailableItems}
              id="cart-continue-checkout-btn"
              className={`w-full py-4 rounded-2xl font-bold text-sm text-center block transition-all ${
                hasUnavailableItems
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-[#0056D2] hover:bg-blue-700 text-white shadow-lg shadow-[#0056D2]/20 cursor-pointer active:scale-95'
              }`}
            >
              {hasUnavailableItems ? 'Remove Sold Out Items' : 'Proceed to Checkout'}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};
